/**
 * KgQueryPanel · Top-of-page query input block for the Knowledge Graph page.
 *
 * Byte-verbatim extraction of the Top Query Input region that used to live at
 * lines 1666-2050 of /app/frontend/src/pages/KnowledgeGraphPage.jsx. Zero
 * visual change · every data-testid preserved · every className and inline
 * style preserved.
 *
 * Extracted 2026-09-01 · IJ-459 · Phase 5A of the KnowledgeGraphPage.jsx split
 * (see /app/memory/design_drafts/kg_page_split_map.md · KgQueryPanel boundary).
 * Umbrella: IJ-150 Refactor and Security Hardening Programme 2026. Owner: E1.
 *
 * Contents:
 *   - Control Centre icon plus its hover label, the restore dot, and the
 *     first-query inline "Heads up" callout.
 *   - Query content region: the quiet-mode chip, the welcome placeholder,
 *     the typed-query span with the magnifier submit button, and the
 *     transparent textarea overlay that carries the real caret.
 *   - Menu icon.
 *   - Predictive completions surface plus the intent-gated typeahead chip
 *     surface, both shown only on the empty home state (queryThread.length 0).
 *
 * Every piece of state, every ref, and both predictive hooks (primaryTA and
 * primaryCompletions) stay owned by the parent and arrive as props. Leaf
 * dependencies are imported directly, mirroring the KgHeader precedent.
 */
import React from 'react';
import { toast } from 'sonner';
import { RestoreTooltipDot } from './RestoreTooltipDot';
import { QuietModeChip } from '../QuietModeChip';
import { CompletionsSurface } from './CompletionsSurface';
import { TypeaheadDropdown } from './TypeaheadDropdown';
import { recordSubmittedQuery } from '../../lib/completionsCorpus';
import { setQuietMode as persistQuietMode, COPY as QUIET_COPY } from '../../lib/quietMode';
import { shouldShowChipSurface } from '../../lib/queryIntent';
import { SHOW_CC_HINTS } from '../../featureFlags';

export const KgQueryPanel = ({
  isMobile,
  topIconsRowRef,
  markControlCentreVisited,
  setShowControlCentre,
  setControlCentreView,
  fetchDataStats,
  isQuerying,
  queryThread,
  controlCentreVisited,
  hiddenTooltips,
  handleHideTooltip,
  handleRestoreTooltip,
  quietMode,
  setQuietModeState,
  query,
  setQuery,
  submitPrimaryQuery,
  handleQueryInputChange,
  queryTextareaRef,
  setShowMenu,
  primaryCompletions,
  primaryTA,
  dataControls,
  setLocationConsentPrompt,
  attemptProductSearch,
}) => {
  return (
    <>
          {/* Top Query Input - Control Centre at left edge, Menu at right edge */}
          <div className={`${isMobile ? 'mt-4' : 'mt-20'} px-4`} ref={topIconsRowRef}>
            <div className={`flex ${isMobile ? 'items-center' : 'items-start'} w-full`}>
              {/* Control Centre icon */}
              <div className="relative group flex-shrink-0 animate-slide-in-left z-50">
                <button onClick={() => { markControlCentreVisited(); setShowControlCentre(true); setControlCentreView('main'); fetchDataStats(); }} className="transition-transform hover:scale-105" data-testid="control-centre-btn">
                  <img src="/icons/control-centre.svg" alt="Control Centre" className={isMobile ? "h-[28px] w-[28px]" : "h-[60px] w-[60px]"} />
                </button>
                {/* D6.0 (Feb 24, 2026) — Suppress the small "Control Centre"
                    hover label whenever the inline callout (below) is
                    visible. Prevents the two from stacking and obstructing
                    the callout header (user screenshot D5_Paul_Pop_Up_Tag).
                    The hover tag auto-restores the moment the callout
                    hides (via Open CC, Open Accuracy Controls — which goes
                    through CC — or Don't Show). */}
                {!isMobile && SHOW_CC_HINTS && !(!isQuerying && queryThread.length >= 1 && !controlCentreVisited && !hiddenTooltips['cc_first_query']) && (
                  <span className="absolute -bottom-12 left-0 px-2 py-1 bg-gray-800 text-xs text-white rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-[100] text-center leading-tight" style={{ width: '70px' }}>Control<br/>Centre</span>
                )}
                {/* Restore dot — appears when CC callout was dismissed by 'Don't Show' */}
                {hiddenTooltips['cc_first_query'] && !isMobile && (
                  <div className="absolute -top-1 -right-1 z-10">
                    <RestoreTooltipDot tooltipId="cc_first_query" hiddenTooltips={hiddenTooltips} onRestore={handleRestoreTooltip} title="Bring back the Control Centre hint" />
                  </div>
                )}
                {/* D2.0 (Feb 23, 2026) — Restored: inline "Heads up — Control
                    Centre" callout tethered to the STATIC top-bar icon. Was
                    previously moved off this anchor to the floating icons,
                    which only appear after >3s idle — leaving most users to
                    never see the prompt on a first-query result that fits
                    in the viewport. Now renders whenever the user has
                    submitted at least one query AND has not yet opened the
                    Control Centre AND has not dismissed the hint. The
                    floating callout in FloatingTopIcons.jsx remains as the
                    fallback for when the user scrolls past this icon. */}
                {/* D2.0 timing refinement (Feb 23, 2026) — Only render AFTER the
                    first query has returned its result. !isQuerying ensures the
                    tag does NOT appear while the LLM is thinking. The tag also
                    auto-hides once the user opens Control Centre (via any
                    entry point) thanks to !controlCentreVisited. */}
                {!isMobile && SHOW_CC_HINTS && !isQuerying && queryThread.length >= 1 && !controlCentreVisited && !hiddenTooltips['cc_first_query'] && (
                  <div
                    className="absolute z-[60] animate-fade-in"
                    style={{ top: '78px', left: '0', width: '320px' }}
                    data-testid="inline-cc-callout"
                  >
                    <div
                      className="rounded-xl shadow-2xl relative"
                      style={{
                        background:
                          'linear-gradient(135deg, rgba(8,30,42,0.97) 0%, rgba(15,40,60,0.97) 100%)',
                        border: '1px solid rgba(34,211,238,0.55)',
                        boxShadow:
                          '0 0 0 1px rgba(34,211,238,0.35), 0 12px 32px rgba(0,0,0,0.6), 0 0 28px rgba(34,211,238,0.25)',
                      }}
                    >
                      {/* Pointer arrow — points up at the Control Centre icon */}
                      <div
                        className="absolute w-3 h-3 rotate-45"
                        style={{
                          left: '22px',
                          top: '-7px',
                          background: 'rgba(8,30,42,0.97)',
                          borderLeft: '1px solid rgba(34,211,238,0.55)',
                          borderTop: '1px solid rgba(34,211,238,0.55)',
                        }}
                      />
                      <div className="p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-cyan-400 text-black text-xs font-bold">
                            !
                          </span>
                          <span className="text-xs uppercase tracking-[0.18em] text-cyan-300 font-semibold">
                            Heads up
                          </span>
                        </div>
                        <p className="text-sm text-gray-100 leading-relaxed">
                          Please check out your{' '}
                          <span className="font-bold text-cyan-300">Control Centre</span>{' '}
                          and your{' '}
                          <span className="font-bold text-cyan-300">Accuracy Controls</span>.
                          We wanted to give you full control over your data and
                          make it as simple as possible at the same time.
                        </p>
                        <div className="mt-3 pt-3 border-t border-cyan-400/20 flex items-center justify-between gap-3">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              markControlCentreVisited();
                              setShowControlCentre(true);
                              setControlCentreView('main');
                              fetchDataStats();
                            }}
                            className="text-xs font-semibold bg-cyan-500 hover:bg-cyan-400 text-black px-3 py-1.5 rounded-md transition-colors"
                            data-testid="inline-cc-callout-open"
                          >
                            Open Control Centre
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleHideTooltip('cc_first_query');
                            }}
                            className="text-[10px] uppercase tracking-wider text-gray-400 hover:text-gray-200 transition-colors"
                            data-testid="inline-cc-callout-dismiss"
                          >
                            Don't Show
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Query content */}
              <div
                className="flex-1 min-w-0 relative cursor-text"
                style={isMobile ? { marginLeft: '8px', marginTop: '0' } : { marginLeft: '40px', marginTop: '12px' }}
                onClick={() => queryTextareaRef.current && queryTextareaRef.current.focus()}
              >
                {/* Lever 6 (Jun 3, 2026) — quiet-mode persistent indicator.
                    Renders only when quietMode === true. Click toggles back to verbose.
                    z-index 20 + relative positioning lifts the chip above the
                    transparent textarea overlay (which sits at z-index ~10) so
                    pointer events land on the chip, not on the textarea. */}
                {quietMode && (
                  <div className="mb-2 relative z-20" onClick={(e) => e.stopPropagation()}>
                    <QuietModeChip
                      active={quietMode}
                      onClick={() => {
                        setQuietModeState(false);
                        persistQuietMode(false);
                        toast.success(QUIET_COPY.quietOffToast, { duration: 4500 });
                      }}
                    />
                  </div>
                )}
                {!query && (
                  <div className="flex items-center text-gray-400 opacity-70" style={{ fontSize: isMobile ? '1rem' : '2.25rem' }}>
                    <span className="blinking-cursor mr-1"></span>
                    <span className={isMobile ? 'truncate' : ''}>{isMobile ? 'Search-It!' : 'Welcome. Search and you will find'}</span>
                    <img src="/icons/magnifier.svg" alt="Search" className={isMobile ? "h-[18px] w-[18px] ml-1 flex-shrink-0" : "h-[36px] w-[36px] ml-2"} />
                  </div>
                )}
                {query && (
                  <div style={{ fontSize: isMobile ? '1rem' : '2.25rem', lineHeight: isMobile ? '1.4rem' : '2.8rem' }}>
                    <span className="text-white" style={{ wordBreak: 'break-word' }}>{query}</span>
                    <button
                      onClick={(e) => { e.stopPropagation(); submitPrimaryQuery(); }}
                      disabled={isQuerying}
                      /*
                       * SPD v5.86 hotfix (2026-02-14) · operator "dead
                       * magnifier click" regression. The transparent-
                       * text <textarea> sibling (D8 three-layer
                       * transparency trick — position: absolute; top: 0;
                       * left: 0; w-full) has no explicit z-index and,
                       * being later in DOM order, wins the CSS stacking
                       * fight against this button. Every click on the
                       * magnifier icon was landing on the textarea
                       * (which has no onClick handler) and vanishing.
                       * Enter still worked because the textarea's
                       * onKeyDown was reachable. `relative z-20` on
                       * the button creates a new stacking context and
                       * lifts it above the textarea so clicks land
                       * where the user aims.
                       */
                      className="ml-2 p-1 transition-transform hover:scale-105 disabled:opacity-50 inline-flex items-center align-middle relative z-20"
                      data-testid="query-search-btn-active"
                    >
                      <img src="/icons/magnifier.svg" alt="Search" className={isMobile ? "h-[20px] w-[20px]" : "h-[32px] w-[32px]"} />
                      {isQuerying && (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className={`animate-spin ${isMobile ? 'h-[18px] w-[18px]' : 'h-[28px] w-[28px]'} border-2 border-cyan-400 border-t-transparent rounded-full`}></div>
                        </div>
                      )}
                    </button>
                  </div>
                )}
                {/* Textarea overlay for typing.
                    D4 fix (Feb 28, 2026): Was a 1×1 px hidden caret-less
                    textarea — meant the user could only edit at the END
                    (no mid-text cursor positioning, no click-to-edit).
                    Now: full-width transparent-text textarea overlaid on
                    top of the visible <span> pill. Span renders the styled
                    text; textarea shows the REAL caret and accepts clicks
                    anywhere along the text. Font metrics match the span
                    exactly so the caret position is pixel-aligned with
                    the visible characters. */}
                <textarea
                  ref={queryTextareaRef}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onInput={handleQueryInputChange}
                  rows={1}
                  className="absolute top-0 left-0 w-full bg-transparent border-none outline-none resize-none"
                  style={{
                    // D8 fix (Feb 28, 2026): three-layer transparency.
                    // `color: transparent` alone was leaving ghost glyphs
                    // visible on Edge/Chromium because of subpixel anti-
                    // aliasing leak. Adding -webkit-text-fill-color +
                    // text-shadow none guarantees zero text rendering
                    // from the textarea — only the styled <span> behind
                    // it shows. Caret remains visible via caretColor.
                    color: 'transparent',
                    WebkitTextFillColor: 'transparent',
                    textShadow: 'none',
                    caretColor: 'white',
                    fontSize: isMobile ? '1rem' : '2.25rem',
                    lineHeight: isMobile ? '1.4rem' : '2.8rem',
                    fontFamily: 'inherit',
                    fontWeight: 'inherit',
                    letterSpacing: 'inherit',
                    padding: 0,
                    margin: 0,
                    paddingRight: isMobile ? '32px' : '56px',
                  }}
                  data-testid="query-textarea"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      // SPD v5.86 hotfix (2026-02-14) — Enter and the
                      // magnifier button both go through the single
                      // `submitPrimaryQuery()` helper so the two entry
                      // points can never diverge again. See the docblock
                      // above `submitPrimaryQuery` for full rationale.
                      submitPrimaryQuery();
                    }
                  }}
                />
              </div>
              
              {/* Menu icon */}
              <div className="relative group flex-shrink-0 animate-slide-in-right">
                <button onClick={() => setShowMenu(true)} className="transition-transform hover:scale-105" data-testid="menu-btn">
                  <img src="/icons/menu.svg" alt="Menu" className={isMobile ? "h-[28px] w-[28px]" : "h-[60px] w-[60px]"} />
                </button>
                {!isMobile && <span className="absolute -bottom-8 left-1/2 -translate-x-1/2 px-2 py-1 bg-gray-800 text-xs text-white rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-[100]">Menu</span>}
              </div>
            </div>

            {/* SPD v5.83 Phase 2 (2026-07-14) — Predictive typeahead now
                consumes the full 12-field JSON contract and mounts the
                matching layout primitive (topic · compare · products ·
                lens · steps · calculator). Design language matches the
                operator-approved /preview/primitives-mockup-v3 route.
                Legacy props left in place as a fall-through for cache
                rows that predate v5.83. */}
            {queryThread.length === 0 && (
              <div className={isMobile ? '' : 'ml-[142px] max-w-[720px]'}>
                {/* SPD v5.87 · Predictive Completions — client-side
                    trie. Renders inline the moment the user starts
                    typing (sub-100 ms). Click a completion → replace
                    + fire the compare search (with location-consent
                    guard). Same wiring as the typeahead onPickCompletion
                    below, but the surface is separate because these
                    completions are prefix-based, not chip-based. */}
                <CompletionsSurface
                  state={primaryCompletions}
                  onPick={(text) => {
                    const defaultSources = ['local', 'online', 'compare'];
                    if (dataControls.location !== 'on') {
                      setQuery(text);
                      setLocationConsentPrompt({ sources: defaultSources, query: text });
                      return;
                    }
                    recordSubmittedQuery(text);
                    setQuery('');
                    attemptProductSearch(text, defaultSources);
                  }}
                  dataTestId="primary-completions"
                />
                {/* SPD v5.87.1 (2026-02-14) — chip surface gated by
                    intent classifier. Hidden for transactional /
                    informational queries where completions already
                    carry the narrowing. Shown only for ambiguous /
                    exploratory queries where the LLM's clarifying
                    question actually adds value. Operator-approved. */}
                {shouldShowChipSurface(query) && (
                <TypeaheadDropdown
                  data={primaryTA}
                  onPickChip={(label) => {
                    // SPD v5.86 (2026-02-14) — chip click fires the
                    // search immediately. No trio picker, no secondary
                    // prompt. Compare is the assumed default. Location-
                    // consent popup appears only if location is off
                    // (single Y/N — Yes flips it on and fires the
                    // search, No dismisses). Same guard as onPickFreetext.
                    //
                    // SPD v5.86 hotfix — case-insensitive dedup so
                    // multi-clicks don't duplicate the label into the
                    // query string (operator's Air Jordans High-top ×3
                    // regression).
                    // SPD v5.86 hotfix — ghost-prompt fix: clear
                    // `query` synchronously before firing so
                    // useTypeahead(query) aborts its in-flight LLM
                    // call and doesn't paint a smart-prompt over the
                    // firing search.
                    const cur = query.trim();
                    const alreadyIncluded = cur.toLowerCase().includes(String(label).toLowerCase());
                    const merged = alreadyIncluded ? cur : `${cur} ${label}`.trim();
                    const defaultSources = ['local', 'online', 'compare'];
                    if (dataControls.location !== 'on') {
                      // Preserve the merge in the bar for the popup
                      // roundtrip; ghost prompt is acceptable here
                      // because the user is looking at the popup.
                      setQuery(merged);
                      setLocationConsentPrompt({ sources: defaultSources, query: merged });
                      return;
                    }
                    setQuery('');
                    attemptProductSearch(merged, defaultSources);
                  }}
                  onPickCompletion={(text) => {
                    // SPD v5.86 hotfix — Completion = replace + fire.
                    // Ghost-prompt guard: clear synchronously so
                    // useTypeahead(query) aborts.
                    const defaultSources = ['local', 'online', 'compare'];
                    if (dataControls.location !== 'on') {
                      setQuery(text);
                      setLocationConsentPrompt({ sources: defaultSources, query: text });
                      return;
                    }
                    recordSubmittedQuery(text);
                    setQuery('');
                    attemptProductSearch(text, defaultSources);
                  }}
                  onPickFreetext={(text) => {
                    // SPD v5.83 · Phase 2d (2026-07-14) — Rule 4 per
                    // operator directive: Enter from the Other freetext
                    // input FIRES the search immediately. No second
                    // prompt, no re-classify round-trip. Compose the
                    // refined query and route through attemptProductSearch
                    // with the SAME default-source + location-consent
                    // logic as the Compare button (Phase 2c rule).
                    // SPD v5.86 hotfix — ghost-prompt guard: clear
                    // synchronously so useTypeahead(query) aborts.
                    const merged = `${query.trim()} ${text}`.trim();
                    const defaultSources = ['local', 'online', 'compare'];
                    if (dataControls.location !== 'on') {
                      setQuery(merged);
                      setLocationConsentPrompt({ sources: defaultSources, query: merged });
                      return;
                    }
                    setQuery('');
                    attemptProductSearch(merged, defaultSources);
                  }}
                />
                )}
              </div>
            )}

            {/* Quota/Demo box is NOT shown on the empty home state — it only
                appears under the first query's bubble and under its
                SuggestionsPanel once a real query has been submitted. */}

            {/* Product/Service Source Options — Below the query, aligned with Welcome text.
                D6-1 (2026-02-13) — operator directive:
                  "This is incorrect. This should only appear as a user is
                   typing and if that query is related to a product or
                   service."
                Restored the intent-gate on `showSourceOptions &&
                detectedSubject` (was removed in D5-2). The follow-up
                strip stays visible per the D4-7 spec but now also gates
                on subject-detection for the follow-up query — see
                KGPage L5410-5430 for the follow-up wiring. */}
            {/* SPD v5.83 · Phase 2c (2026-07-14) — Compare is highlighted
                as the DEFAULT source when the user's location is ON. When
                location is OFF and the user clicks Support Local (or the
                Compare button, which also needs local data), we intercept
                and show the location-consent popup instead of firing the
                search. Online / Big Players stays unchanged — it doesn't
                need location. */}
            {/* SPD v5.86 (2026-02-14) — Trio strip (Support Local ·
                Online / Big Players · Compare) DELETED per operator
                directive. Compare is now the assumed default for every
                transactional query; the trio buttons only added click-
                friction without offering the user genuine choice they
                weren't already exercising via the typeahead chips. All
                three intents (pricing / comparison / tier-model) roll
                into a single Compare-flavoured product search which
                surfaces local + big-player results side-by-side. Any
                contextual add-ons ("older model 50% off around the
                corner from where you are now") land inside the result
                payload, not as pre-search source picking. */}
          </div>
    </>
  );
};

export default KgQueryPanel;
